import { apiRequest, api } from '@/lib/api';
import type { AccountantShareMode } from './schemas';

export interface AccountantDTO {
  id: string;
  userId: string;
  name: string;
  email: string;
  shareMode: AccountantShareMode;
  monthlyPackageDay: number;
  readonlyToken: string | null;
  readonlyTokenExpiresAt: string | null;
  lastSharedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountantShareDTO {
  id: string;
  accountantId: string;
  referenceMonth: string;
  channel: 'EMAIL_PACKAGE' | 'READONLY_LINK';
  payloadSnapshot: Record<string, unknown>;
  sentAt: string;
}

export interface GenerateLinkResult {
  accountant: AccountantDTO;
  url: string;
}

export interface CreateAccountantPayload {
  name: string;
  email: string;
  shareMode: AccountantShareMode;
  monthlyPackageDay: number;
}

export type UpdateAccountantPayload = Partial<CreateAccountantPayload>;

export const accountantApi = {
  list: () => api.get<AccountantDTO[]>('/accountants'),
  get: (id: string) => api.get<AccountantDTO>(`/accountants/${id}`),
  create: (payload: CreateAccountantPayload) =>
    api.post<AccountantDTO>('/accountants', payload),
  update: (id: string, payload: UpdateAccountantPayload) =>
    api.patch<AccountantDTO>(`/accountants/${id}`, payload),
  remove: (id: string) =>
    apiRequest<void>({ method: 'DELETE', path: `/accountants/${id}` }),
  generateLink: (id: string) =>
    api.post<GenerateLinkResult>(`/accountants/${id}/generate-readonly-link`),
  rotateToken: (id: string) =>
    api.post<GenerateLinkResult>(`/accountants/${id}/rotate-token`),
  listShares: (id: string) =>
    api.get<AccountantShareDTO[]>(`/accountants/${id}/shares`),
};

export interface PublicAccountantView {
  accountant: {
    id: string;
    name: string;
    shareMode: AccountantShareMode;
    expiresAt: string | null;
  };
  owner: { id: string; name: string; email: string };
  month: string;
  summary: {
    month: string;
    cnpjId: string | null;
    total: number;
    shiftsCount: number;
    itemsCount: number;
    byService: Array<{
      serviceId: string;
      name: string;
      color: string;
      shiftsCount: number;
      itemsCount: number;
      total: number;
      percentage: number;
    }>;
    byExam: Array<{
      examTypeId: string;
      serviceId: string;
      name: string;
      shortName: string;
      unitValue: number;
      qty: number;
      subtotal: number;
    }>;
    monthlySeries: Array<{ month: string; total: number }>;
  };
  fiscalRecords: Array<{
    record: {
      id: string;
      referenceMonth: string;
      computedProduction: number;
      reportedValue: number | null;
      status: string;
      paidAt: string | null;
    };
    service: { id: string; name: string; color: string } | null;
  }>;
}

export const publicAccountantApi = {
  view: (token: string, month: string) =>
    api.get<PublicAccountantView>(`/public/accountant-view/${token}`, { month }),
};
